<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    class="rounded border border-solid border-gray-400 p-3"
    :class="isFreeTrial && 'bg-yellow-400/10'"
  >
    <button
      class="flex w-full cursor-pointer items-center justify-between text-left"
      @click="handleToggle"
    >
      <div class="flex-1 pr-4">
        <p class="mb-1 break-words text-lg font-bold tablet:text-xl">
          {{ planData.name }}
        </p>
        <p class="mb-1 text-sm text-gray-600">{{ planData.partNumber }}</p>
        <MostPopularTag v-if="planData.isMostPopular" />
      </div>
      <div class="h-6 w-6 basis-6">
        <IconCaret
          :direction="isShow ? 'up' : 'down'"
          class="h-full w-full text-blue-400"
        />
      </div>
    </button>
    <div v-show="isShow">
      <div class="py-3">
        <p class="mb-1 font-bold">{{ t('details') }}</p>
        <div
          class="markdown-content"
          v-html="provideMarkdownHtml(planData.description)"
        />
      </div>
      <AreaPricingPriceItem :plan-data="planData" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    EnumBillingCycle,
    EnumLicenseDeliveryMethod,
    EnumServiceType,
  } from '@/assets/types/api/Products';
  import MostPopularTag from '@/components/Area/Pricing/MostPopularTag.vue';
  import type { IPricePlan } from '@/stores/pricing/pricingStore';

  interface IProps {
    defaultShow: boolean;
    planData: IPricePlan;
  }

  const props = withDefaults(defineProps<IProps>(), {
    defaultShow: false,
    planData: () => {
      return {
        name: '',
        partNumber: '',
        description: '',
        price: null,
        currency: '',
        billingCycleId: EnumBillingCycle.ONE_TIME_PURCHASE,
        licenseDeliveryMethodId: EnumLicenseDeliveryMethod.EMAIL_LICENSE,
        isMostPopular: false,
        isValidRegion: false,
        isShowPrice: false,
        isAccountPrice: false,
        serviceType: EnumServiceType.CLOUD_ENSAAS,
      };
    },
  });

  const route = useRoute();
  const { t } = useI18n({ useScope: 'local' });

  const isShow = ref<boolean>(props.defaultShow);

  const isFreeTrial = computed((): boolean => {
    return props.planData.billingCycleId === EnumBillingCycle.FREE_TRIAL;
  });

  const handleToggle = (): void => {
    isShow.value = !isShow.value;
  };

  onMounted(() => {
    // 若 URL Hash 為 #free-trial-plan 且此方案為 freeTrial 則預設展開
    if (isFreeTrial.value && route.hash === '#free-trial-plan') {
      isShow.value = true;
    }
  });
</script>

<style scoped lang="scss">
  .markdown-content {
    :deep(ul),
    :deep(ol),
    :deep(p),
    :deep(a) {
      @apply text-black;
    }
  }
</style>

<i18n lang="json">
{
  "en-int": {
    "details": "Details"
  },
  "en-jp": {
    "details": "Details"
  },
  "en-tw": {
    "details": "Details"
  },
  "en-kr": {
    "details": "Details"
  },
  "en-americas": {
    "details": "Details"
  },
  "en-asia-pacific": {
    "details": "Details"
  },
  "en-emea": {
    "details": "Details"
  },
  "ja-jp": {
    "details": "詳細"
  },
  "zh-cn": {
    "details": "详细信息"
  },
  "zh-tw": {
    "details": "細節"
  }
}
</i18n>
